import React from 'react'
import { useForm, Link, usePage } from '@inertiajs/react'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import ManagementLayout from '@/components/layouts/ManagementLayout'
import type { Account } from '@/types'

interface Props {
  member: {
    email: string
    role: string
  }
}

export default function AccountUsersNew({ member }: Props) {
  const { currentAccount } = usePage<{ currentAccount: Account }>().props

  const { data, setData, post, processing, errors } = useForm({
    email: member.email || '',
    role: member.role || 'member',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/accounts/${currentAccount.slug}/members`)
  }

  return (
    <ManagementLayout>
      <Box sx={{ maxWidth: 672, mx: 'auto' }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={600}>
            Add Team Member
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Invite a user to join this account.
          </Typography>
        </Box>

        <Paper sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              value={data.email}
              onChange={(e) => setData('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email || 'Enter the email address of the user you want to invite.'}
              placeholder="user@example.com"
              sx={{ mb: 3 }}
            />

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={data.role}
                label="Role"
                onChange={(e) => setData('role', e.target.value)}
              >
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 3 }}>
              <Typography variant="body2" fontWeight={500}>Role Permissions:</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Admin:</strong> Full access to manage backends, credentials, virtual buckets, and team members.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                <strong>Member:</strong> Can view all resources but cannot make changes.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Button component={Link} href={`/accounts/${currentAccount.slug}/members`}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={processing}>
                Add Member
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </ManagementLayout>
  )
}
